import { Box, Chip, Container, Stack } from "@mui/material";

import MainLayout from "../../ui/layouts/main";

import { OffersListWidget } from "../../ui/widgets/OffersListWidget/OffersListWidget";
import { SidebarFilterWidgetLazy } from "../../ui/widgets/SidebarFilterWidget/SidebarFilterWidgetLazy";
import { SidebarFilterButtonWidget } from "../../ui/widgets/SidebarFilterWidget/SidebarFilterButtonWidget";
import SidebarSortWidget from "../../ui/widgets/SidebarFilterWidget/SidebarSortWidget";
import FormSearch from "../../ui/components/FormSearch/FormSearch";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  dateRangeAvailableSelector,
  filterSelectedAttributeIdsAtom,
  selectedHotelAtom,
} from "../../state/search-state";
import { UnitGroupListWidget } from "../../ui/widgets/UnitGroupListWidget/UnitGroupListWidget";
import { useGetAllUnitAttributes } from "../../api/hooks";
import { composeServerSideProps } from "next-composition";
import { withTranslations } from "../../server/ssrprops/withTranslations";
import { useInitialStateFromQuery } from "../../hooks/useInitialStateFromQuery";
import { useEffect } from "react";
import dataTestIds from "../../utils/data-test-ids";
import { withBannersSearchSsrProps } from "../../server/ssrprops/withBannerSearchSsrProps";
import { withSeoSsrProps } from "../../server/ssrprops/withSeoSsrProps";
import { BannerSearchListResponseDataItem } from "../../api/content-client/service";

SearchPage.getLayout = (page: React.ReactElement) => (
  <MainLayout
    renderHeaderContent={() => (
      <>
        <Stack pl={2} direction={"row"} spacing={2} alignItems={"center"}>
          <FormSearch type={"compact"} />
          <SidebarFilterButtonWidget type={"compact"} />
          <SidebarSortWidget />
        </Stack>
      </>
    )}
  >
    <Box pb={4}>{page}</Box>
  </MainLayout>
);

type SearchPageProps = {
  bannersSearch: BannerSearchListResponseDataItem[];
};

function SearchPage({ bannersSearch }: SearchPageProps) {
  const dateRangeIsAvailable = useRecoilValue(dateRangeAvailableSelector);

  const hotelId = useRecoilValue(selectedHotelAtom);

  const [selectedAttributes, setSelectedAttributes] = useRecoilState(filterSelectedAttributeIdsAtom);
  const { data: allUnitAttributes = [] } = useGetAllUnitAttributes();

  useEffect(() => {
    return () => {
      setSelectedAttributes([]);
    };
  }, []);

  useInitialStateFromQuery();

  return (
    <>
      <Container maxWidth={"lg"}>
        <Stack
          sx={{ minHeight: 30 }}
          mb={2}
          spacing={1}
          direction="row"
          flexWrap="wrap"
          useFlexGap
          alignItems={"center"}
        >
          {allUnitAttributes
            .map((g) => g.attributes)
            .flat()
            .filter((a) => selectedAttributes.find((s) => s === a.id))

            .map((attr) => (
              <Chip
                key={`${attr.id}`}
                label={attr.title}
                size="small"
                onDelete={() =>
                  setSelectedAttributes((attrs) => {
                    return attrs.filter((id) => id !== attr.id);
                  })
                }
              />
            ))}
        </Stack>
      </Container>
      <Container maxWidth={"lg"}>
        {dateRangeIsAvailable ? (
          <OffersListWidget
            bannersSearch={bannersSearch}
            bannerBlockId="searchPage"
            showTotalPrice
            withGuestsContext
            withAttributesContext
            hotelIdContext={hotelId}
            perPage={12}
            dataTestId={dataTestIds.PAGES.SEARCH.UNITS_CONTAINER}
          />
        ) : (
          <UnitGroupListWidget
            // TODO: to add support for bannersSearch
            // bannersSearch={bannersSearch}
            // bannerBlockId="searchPage"
            withGuestsContext
            withAttributesContext
            hotelIdContext={hotelId}
            perPage={12}
            dataTestId={dataTestIds.PAGES.SEARCH.UNITS_CONTAINER}
          />
        )}
      </Container>

      <SidebarFilterWidgetLazy />
    </>
  );
}

export default SearchPage;

export const getServerSideProps = composeServerSideProps({
  use: [withBannersSearchSsrProps, withTranslations, withSeoSsrProps],
});
